/**
 * Absence Insights API Service
 *
 * Calls the People Space proxy's restricted-DWH endpoints. The proxy
 * authorizes `space.people.absence_insights.view` server-side (always
 * enforced), so a caller without access gets a 403 regardless of the UI.
 */
import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-people-520260363689.europe-west3.run.app';

export type AbsenceType = 'all' | 'sl' | 'csl';

export interface AbsenceMonthlyPoint {
  year: number;
  month: number;
  monthLabel: string;
  employeeCount: number;
  /** True when employeeCount fell below the GDPR small-cell threshold — metric fields below are nulled, not real zeros. */
  suppressed: boolean;
  sickLeaveDays: number | null;
  childCareDays: number | null;
  totalAbsenceDays: number | null;
  availableWorkingDays: number | null;
  absenceRatePct: number | null;
}

/**
 * One org entity's chronological points[] series — the drill-down levels
 * for the Overview tab's org trend chart. `team`/`subteam` are only present
 * at the corresponding depth (a department-level entry has no `team`).
 */
export interface AbsenceOrgMonthlySeries {
  department: string;
  team?: string;
  subteam?: string;
  points: AbsenceMonthlyPoint[];
}

export interface AbsenceTenureBreakdown {
  tenureBand: string;
  employeeCount: number;
  /** True when employeeCount fell below the GDPR small-cell threshold — metric fields below are nulled, not real zeros. */
  suppressed: boolean;
  sickLeaveDays: number | null;
  childCareDays: number | null;
  totalAbsenceDays: number | null;
  availableWorkingDays: number | null;
  absenceRatePct: number | null;
}

export interface AbsenceSummary {
  periodMonths: number;
  employeeCount: number;
  /** Mean of each month's own headcount — stable across different-length ranges, unlike employeeCount. */
  avgEmployeeCount: number;
  /** True when the overall filtered population fell below the GDPR small-cell threshold — metric fields below are nulled, not real zeros. */
  suppressed: boolean;
  totalAbsenceDays: number | null;
  sickLeaveDays: number | null;
  childCareDays: number | null;
  availableWorkingDays: number | null;
  avgAbsenceRatePct: number | null;
}

/** Raw absence-day counts by weekday — no rate/percentage, since there's no working-days-per-weekday denominator upstream yet. */
export interface AbsenceDayOfWeekPoint {
  dayName: string;
  /** True when the overall filtered population fell below the GDPR small-cell threshold — metric fields below are nulled, not real zeros. */
  suppressed: boolean;
  sickLeaveDays: number | null;
  childCareDays: number | null;
  totalAbsenceDays: number | null;
}

export interface AbsencePeriod {
  summary: AbsenceSummary;
  monthlyTrend: AbsenceMonthlyPoint[];
  monthlyByDepartment: AbsenceOrgMonthlySeries[];
  monthlyByTeam: AbsenceOrgMonthlySeries[];
  monthlyBySubteam: AbsenceOrgMonthlySeries[];
  byTenure: AbsenceTenureBreakdown[];
  byDayOfWeek: AbsenceDayOfWeekPoint[];
}

export interface AbsenceInsights extends AbsencePeriod {
  priorYear: AbsencePeriod;
  filters: {
    from: string;
    to: string;
    departments: string[];
    teams: string[];
    subteams: string[];
    supervisors: string[];
    contractTypes: string[];
    employeeStatuses: string[];
  };
  table: string;
  project: string;
  generatedAt: string;
}

export interface AbsenceFilterOptions {
  departments: string[];
  teams: { department: string; team: string }[];
  subteams: { department: string; team: string; subteam: string }[];
  supervisors: string[];
  contractTypes: string[];
  employeeStatuses: string[];
}

export interface AbsenceInsightsQuery {
  /** "YYYY-MM", inclusive. Defaults to 12 months before `to` server-side. */
  from?: string;
  /** "YYYY-MM", inclusive. Defaults to the current month server-side. */
  to?: string;
  departments?: string[];
  teams?: string[];
  subteams?: string[];
  supervisors?: string[];
  contractTypes?: string[];
  employeeStatuses?: string[];
  /** Employees route only — ignores from/to entirely and uses the fixed trailing-12-month window instead (same one the "Employees with 10+ Days" KPI uses). */
  rolling12?: boolean;
}

export interface AbsenceEmployeeMonthlyPoint {
  year: number;
  month: number;
  monthLabel: string;
  sickLeaveDays: number;
  childCareDays: number;
  totalAbsenceDays: number;
  availableWorkingDays: number;
}

export interface AbsenceEmployeeRecord {
  employeeId: string;
  employeeName: string;
  department: string;
  team: string;
  subteam: string;
  supervisor: string | null;
  contractType: string | null;
  employeeStatus: string | null;
  tenureBand: string;
  monthly: AbsenceEmployeeMonthlyPoint[];
  periodTotalDays: number;
  periodAbsenceRatePct: number;
  /** Same-window totals from 12 months earlier, or null if this employee has no rows there (e.g. not yet hired). */
  priorYear: {
    sickLeaveDays: number;
    childCareDays: number;
    totalAbsenceDays: number;
    availableWorkingDays: number;
  } | null;
}

export interface AbsenceEmployeesResponse {
  employees: AbsenceEmployeeRecord[];
  /** "YYYY-MM" — earliest month the Employees tab will ever return, per the 24-month GDPR data-minimisation policy. Always present, independent of whether this particular response was truncated. */
  retentionCutoff: string;
  /** True when the requested "from" was older than retentionCutoff and got clamped up to it. */
  retentionTruncated: boolean;
  filters: {
    from: string;
    to: string;
    /** True when this response reflects the fixed rolling-12-month window, not the requested from/to. */
    rolling12: boolean;
    departments: string[];
    teams: string[];
    subteams: string[];
    supervisors: string[];
    contractTypes: string[];
    employeeStatuses: string[];
  };
  table: string;
  project: string;
  generatedAt: string;
}

/** Thrown when the proxy denies access (403) so the UI can show a clear message. */
export class AbsenceAccessError extends Error {}

function buildQueryString(query: AbsenceInsightsQuery): string {
  const params = new URLSearchParams();
  if (query.from) params.set('from', query.from);
  if (query.to) params.set('to', query.to);
  if (query.departments?.length) params.set('department', query.departments.join(','));
  if (query.teams?.length) params.set('team', query.teams.join(','));
  if (query.subteams?.length) params.set('subteam', query.subteams.join(','));
  if (query.supervisors?.length) params.set('supervisor', query.supervisors.join(','));
  if (query.contractTypes?.length) params.set('contractType', query.contractTypes.join(','));
  if (query.employeeStatuses?.length) params.set('employeeStatus', query.employeeStatuses.join(','));
  if (query.rolling12) params.set('rolling12', 'true');
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

async function getJson<T>(path: string): Promise<T> {
  const res = await authenticatedFetch(`${PROXY_URL}${path}`);
  if (res.status === 403) {
    throw new AbsenceAccessError('You do not have access to absence insights.');
  }
  if (!res.ok) {
    throw new Error(`Failed to fetch absence data (${res.status})`);
  }
  return res.json();
}

export function fetchAbsenceInsights(query: AbsenceInsightsQuery = {}): Promise<AbsenceInsights> {
  return getJson<AbsenceInsights>(`/api/absence/insights${buildQueryString(query)}`);
}

export function fetchAbsenceFilterOptions(): Promise<AbsenceFilterOptions> {
  return getJson<AbsenceFilterOptions>('/api/absence/filters');
}

/**
 * Named, per-employee monthly data — gated by a separate, stricter
 * permission (`space.people.absence_insights.view_employees`) than
 * fetchAbsenceInsights/fetchAbsenceFilterOptions. A caller with access to
 * the aggregate dashboard does NOT automatically get this.
 */
export function fetchAbsenceEmployees(query: AbsenceInsightsQuery = {}): Promise<AbsenceEmployeesResponse> {
  return getJson<AbsenceEmployeesResponse>(`/api/absence/employees${buildQueryString(query)}`);
}
